/**
 * „Feedback senden" — das Formular aus dem Kontomenü (HM-169, ADR 0047).
 *
 * Aufbau nach `ui_kits/dashboard/Feedback senden.html`: Art, Titel,
 * Beschreibung, darunter eingeklappt „Wird mitgesendet". Senden geht erst,
 * wenn Titel und Beschreibung gefüllt sind; danach steht an derselben Stelle
 * die Bestätigung.
 *
 * **„Wird mitgesendet" kommt vom Server** (`GET /feedback/context`) und nicht
 * aus dieser Datei: App-Fassung und Browser legt die API aus der Kennung der
 * Anfrage bei. Was hier stünde, wäre eine Behauptung über eine Zeile, die
 * jemand anderes schreibt. Wählbar ist allein die Anlage — sie ist vorgewählt
 * und lässt sich abwählen.
 *
 * **Übernommen ist der Aufbau, nicht jede Einzelheit.** Der Entwurf zeichnet
 * den Dialog schmal als Blatt von unten; hier ist es der `Dialog` des
 * Design-Systems, der auf schmalen Fenstern die Breite füllt. Ein zweiter
 * Dialog nur für diese Stelle wäre die zweite Fassung einer Komponente.
 */
import { useMutation } from "@tanstack/react-query";
import { Alert, Button, Checkbox, Dialog, Input, SegmentedControl, Textarea, icons } from "@hausmeter/ui";
import type { UsageScreen } from "@hausmeter/domain";
import { useId, useState, type ReactNode } from "react";
import { api } from "../lib/client.js";
import { apiErrorMessage } from "../lib/api-error.js";
import {
  FEEDBACK_BODY_MAX,
  FEEDBACK_HINTS,
  FEEDBACK_KIND_ACTIONS,
  FEEDBACK_TITLE_MAX,
  feedbackReady,
} from "../lib/feedback.js";
import { useFeedbackContext } from "../lib/queries.js";
import type { FeedbackKindFilter } from "../lib/router.js";

export interface FeedbackDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Die Ansicht, aus der das Menü geöffnet wurde; `null`, wenn sie keinen Schlüssel hat. */
  readonly page: UsageScreen | null;
  /** Die im Umschalter gewählte Anlage; `null` in der Wohnung eines Bewohners. */
  readonly site: { readonly id: string; readonly name: string } | null;
}

export function FeedbackDialog({ open, onClose, page, site }: FeedbackDialogProps) {
  const [kind, setKind] = useState<FeedbackKindFilter>("bug");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [withSite, setWithSite] = useState(true);
  const [attachedOpen, setAttachedOpen] = useState(false);
  const context = useFeedbackContext(page, open);
  const attachedId = useId();

  const send = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.POST("/feedback", {
        body: {
          kind,
          title: title.trim(),
          body: body.trim(),
          ...(page === null ? {} : { page }),
          ...(site !== null && withSite ? { siteId: site.id } : {}),
        },
      });
      if (error || !data) throw new Error(apiErrorMessage(error) ?? "Senden fehlgeschlagen.");
      return data;
    },
  });

  function close(): void {
    onClose();
    // Nach dem Senden ist das Formular leer; abgebrochen bleibt der Text stehen —
    // wer versehentlich danebenklickt, soll ihn nicht neu schreiben müssen.
    if (send.isSuccess) {
      setTitle("");
      setBody("");
      setKind("bug");
      setWithSite(true);
      setAttachedOpen(false);
    }
    send.reset();
  }

  const ready = feedbackReady(title, body) && !send.isPending;
  const attachedCount = 4 + (site !== null && withSite ? 1 : 0);

  if (send.isSuccess) {
    return (
      <Dialog open={open} onClose={close} title="Feedback senden" width={520}>
        <div role="status" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14, padding: "10px 0 6px", textAlign: "center" }}>
          <span
            style={{
              display: "inline-flex",
              width: 48,
              height: 48,
              borderRadius: "50%",
              alignItems: "center",
              justifyContent: "center",
              background: "var(--hm-green-50)",
              color: "var(--hm-green-700)",
            }}
          >
            <icons.IconCheck size={24} />
          </span>
          <div style={{ fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 600, color: "var(--text-strong)" }}>
            Danke, ist angekommen.
          </div>
          <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)", maxWidth: 360 }}>
            Wir lesen jede Meldung. Eine Antwort oder Benachrichtigung über den Fortgang gibt es bislang nicht.
          </p>
          <Button variant="secondary" onClick={close}>
            Schließen
          </Button>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      title="Feedback senden"
      width={520}
      footer={
        <>
          <Button variant="secondary" onClick={close}>
            Abbrechen
          </Button>
          <Button disabled={!ready} onClick={() => send.mutate()}>
            {send.isPending ? "Wird gesendet …" : "Senden"}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (ready) send.mutate();
        }}
        style={{ display: "flex", flexDirection: "column", gap: 16 }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-body)" }}>Art</span>
          <SegmentedControl
            label="Art"
            fill
            value={kind}
            onChange={setKind}
            options={[
              { value: "bug", label: FEEDBACK_KIND_ACTIONS.bug },
              { value: "wish", label: FEEDBACK_KIND_ACTIONS.wish },
            ]}
          />
        </div>
        <Input
          label="Titel"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder={FEEDBACK_HINTS[kind].title}
          maxLength={FEEDBACK_TITLE_MAX}
        />
        <Textarea
          label="Beschreibung"
          value={body}
          onChange={(event) => setBody(event.target.value)}
          placeholder={FEEDBACK_HINTS[kind].body}
          maxLength={FEEDBACK_BODY_MAX}
        />

        <div style={{ border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", background: "var(--surface-inset)" }}>
          <button
            type="button"
            aria-expanded={attachedOpen}
            aria-controls={attachedId}
            onClick={() => setAttachedOpen((value) => !value)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              width: "100%",
              padding: "11px 14px",
              border: "none",
              background: "transparent",
              cursor: "pointer",
              textAlign: "left",
              fontFamily: "var(--font-body)",
              color: "var(--text-strong)",
            }}
          >
            <span style={{ display: "inline-flex", transform: attachedOpen ? "rotate(90deg)" : "none", transition: "transform .15s", color: "var(--text-muted)" }}>
              <icons.IconChevronRight size={15} />
            </span>
            <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600 }}>Wird mitgesendet</span>
            {!attachedOpen && <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{attachedCount} Angaben</span>}
          </button>
          {attachedOpen && (
            <div id={attachedId} style={{ padding: "0 14px 12px" }}>
              <div style={{ fontSize: 12.5, lineHeight: 1.5, color: "var(--text-muted)", paddingBottom: 8 }}>
                Diese Angaben legt die App bei, damit sich die Meldung nachvollziehen lässt. Nehmen wir sie an, geht sie ohne Absender und Anlage in unser Entwicklungssystem.
              </div>
              {context.isError ? (
                <Alert tone="warning">{context.error.message}</Alert>
              ) : (
                <>
                  <Attached label="Absender" value={context.data?.from} />
                  <Attached label="App-Fassung" value={context.data?.app} mono />
                  <Attached label="Browser" value={context.data?.client} />
                  <Attached label="Aktuelle Seite" value={context.data?.page} />
                </>
              )}
              {site !== null && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(96px,132px) minmax(0,1fr)",
                    gap: 12,
                    padding: "8px 0 0",
                    fontSize: 13,
                    borderTop: "1px solid var(--border-subtle)",
                    alignItems: "center",
                  }}
                >
                  <span style={{ color: "var(--text-muted)" }}>Anlage</span>
                  <Checkbox checked={withSite} onChange={setWithSite} label={site.name} />
                </div>
              )}
            </div>
          )}
        </div>

        {send.isError && <Alert tone="danger">{send.error.message}</Alert>}
        {/* Enter im Titel sendet — ohne diesen Knopf hätte das Formular keinen Absender. */}
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </Dialog>
  );
}

/** Eine Zeile unter „Wird mitgesendet". Ohne Wert steht „nicht erkannt" — die Zeile fehlt nicht. */
function Attached({ label, value, mono = false }: { readonly label: string; readonly value: string | null | undefined; readonly mono?: boolean }) {
  const shown: ReactNode = value === undefined ? "…" : value === null ? "nicht erkannt" : value;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(96px,132px) minmax(0,1fr)",
        gap: 12,
        padding: "7px 0",
        fontSize: 13,
        borderTop: "1px solid var(--border-subtle)",
      }}
    >
      <span style={{ color: "var(--text-muted)" }}>{label}</span>
      <span
        style={{
          color: value === null || value === undefined ? "var(--text-faint)" : "var(--text-strong)",
          fontFamily: mono ? "var(--font-mono)" : "inherit",
          fontSize: mono ? 12.5 : 13,
          overflowWrap: "anywhere",
        }}
      >
        {shown}
      </span>
    </div>
  );
}
